import { Flex, useMantineTheme } from '@mantine/core';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IGeoFilter } from '@feathr/blackbox';
import { AsyncDisplay, Pill, Text } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

interface IGeoFilterWithIncluded extends IGeoFilter {
  included: boolean;
}

interface IGeoFiltersListProps {
  includedGeoFilterIds: string[];
  excludedGeoFilterIds: string[];
}

function GeoFiltersList({
  includedGeoFilterIds,
  excludedGeoFilterIds,
}: Readonly<IGeoFiltersListProps>): JSX.Element {
  const { t } = useTranslation();
  const { GeoFilters } = useStore();
  const [geoFilters, setGeoFilters] = useState<IGeoFilterWithIncluded[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useMantineTheme();

  useEffect(() => {
    const allGeoFilterIds = [...includedGeoFilterIds, ...excludedGeoFilterIds];

    if (!allGeoFilterIds.length) {
      setGeoFilters([]);
      return;
    }

    setIsLoading(true);

    const fetchGeoFilters = async (): Promise<void> => {
      try {
        const geoFilters = GeoFilters.list({
          filters: {
            id__in: allGeoFilterIds,
          },
          pagination: {
            page: 0,
            page_size: 50,
          },
        });

        await when(() => !geoFilters.isPending);

        const geoFiltersWithIncluded: IGeoFilterWithIncluded[] = geoFilters.models.map(
          (geoFilter) => ({
            ...geoFilter.toJS(),
            included: includedGeoFilterIds.includes(geoFilter.id),
          }),
        );

        setGeoFilters(geoFiltersWithIncluded);
      } catch (error) {
        setGeoFilters([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGeoFilters();
  }, [includedGeoFilterIds, excludedGeoFilterIds, GeoFilters]);

  return (
    <AsyncDisplay isLoading={isLoading}>
      {geoFilters.length > 0 && (
        <Flex direction={'column'} gap={theme.spacing[2]}>
          {geoFilters.map((geoFilter) => (
            <Flex align={'center'} justify={'space-between'} key={geoFilter.id}>
              <Flex align={'center'} gap={theme.spacing[2]}>
                <Text.DefaultSemibold>{geoFilter.name}</Text.DefaultSemibold>
                <Text.DefaultRegular>
                  {geoFilter.address.region && `${geoFilter.address.region}, `}
                  {geoFilter.address.country}
                </Text.DefaultRegular>
              </Flex>
              <Flex align={'center'} gap={theme.spacing[2]}>
                <Text.SmallRegular>{geoFilter.kind}</Text.SmallRegular>
                <Pill theme={geoFilter.included ? 'green' : 'red'}>
                  {geoFilter.included ? t('Included') : t('Excluded')}
                </Pill>
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}
    </AsyncDisplay>
  );
}

export default observer(GeoFiltersList);
